import React, { useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import { getApiBaseUrl } from '../../apolloClient';
import { useAuth } from '../../context/AuthContext';
import { formatDate, formatMoney } from '../../utils/format';
import EmptyState from '../ui/EmptyState';
import LoadingState from '../ui/LoadingState';
import Section from '../ui/Section';
import StatusChip from '../ui/StatusChip';
import { billingCurrency, invoiceStatusMeta } from './billingModel';
import type { Invoice } from './billingTypes';

interface InvoicesSectionProps {
  invoices: Invoice[];
  loading: boolean;
  error: boolean;
  onRetry: () => void;
}

// Date | Invoice | Amount | Status | action, from md up; stacked rows below.
// Every column has a fixed width (or the one 1fr), because the header and
// each row are separate grids: an `auto` column would size differently in each.
const ROW_COLUMNS = { md: '128px minmax(0, 1fr) 120px 112px 140px' };

/** Receipts for every payment, newest first, each downloadable as a PDF. */
const InvoicesSection: React.FC<InvoicesSectionProps> = ({ invoices, loading, error, onRetry }) => {
  const { user } = useAuth();
  const [downloading, setDownloading] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);

  const download = async (invoice: Invoice) => {
    setDownloadError(null);
    if (!user) {
      setDownloadError('Your session has expired. Sign in again to download invoices.');
      return;
    }
    setDownloading(invoice.id);
    try {
      const token = await user.getIdToken(true);
      const url = invoice.downloadUrl?.startsWith('http') ? invoice.downloadUrl : `${getApiBaseUrl()}${invoice.downloadUrl}`;
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!response.ok) throw new Error(`Invoice download failed (${response.status})`);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `${invoice.invoiceNumber || 'invoice'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Failed to download invoice:', err);
      setDownloadError(`We couldn't download invoice ${invoice.invoiceNumber}. Please try again.`);
    } finally {
      setDownloading(null);
    }
  };

  let body: React.ReactNode;
  if (loading && invoices.length === 0) {
    body = <LoadingState variant="list" count={2} label="Loading invoices" />;
  } else if (error && invoices.length === 0) {
    body = (
      <Box sx={{ p: 2.5 }}>
        <Alert
          severity="error"
          action={
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          }
        >
          We couldn&apos;t load your invoices.
        </Alert>
      </Box>
    );
  } else if (invoices.length === 0) {
    body = (
      <EmptyState
        compact
        icon={<ReceiptLongOutlinedIcon />}
        title="No invoices yet"
        description="A receipt appears here after each payment."
      />
    );
  } else {
    body = (
      <Box role="table" aria-label="Invoices">
        <Box
          role="row"
          sx={{
            display: { xs: 'none', md: 'grid' },
            gridTemplateColumns: ROW_COLUMNS,
            gap: 2,
            px: 2.5,
            py: 1,
            bgcolor: 'grey.100',
          }}
        >
          {['Date', 'Invoice', 'Amount', 'Status'].map((heading) => (
            <Typography role="columnheader" key={heading} variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>
              {heading}
            </Typography>
          ))}
          <Box role="columnheader" aria-label="Download" />
        </Box>
        {invoices.map((invoice) => {
          const meta = invoiceStatusMeta(invoice.status);
          return (
            <Box
              role="row"
              key={invoice.id || invoice.invoiceNumber}
              data-testid={`invoice-row-${invoice.invoiceNumber}`}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: 'minmax(0, 1fr) auto', md: ROW_COLUMNS.md },
                alignItems: 'center',
                columnGap: 2,
                rowGap: 0.5,
                px: 2.5,
                py: 1.5,
                borderColor: 'divider',
                '&:not(:last-of-type)': { borderBottom: 1, borderColor: 'divider' },
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              <Typography role="cell" variant="body2" color="text.secondary" sx={{ order: { xs: 3, md: 0 } }}>
                {formatDate(invoice.createdAt)}
              </Typography>
              <Typography role="cell" variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere', order: { xs: 1, md: 0 } }}>
                {invoice.invoiceNumber}
              </Typography>
              <Typography role="cell" variant="body2" sx={{ order: { xs: 2, md: 0 }, textAlign: { xs: 'right', md: 'left' } }}>
                {formatMoney(Number(invoice.amount) || 0, billingCurrency(invoice.currency))}
              </Typography>
              <Box role="cell" sx={{ order: { xs: 4, md: 0 }, justifySelf: { xs: 'end', md: 'start' } }}>
                <StatusChip kind="billing" status={invoice.status} label={meta.label} tone={meta.tone} />
              </Box>
              <Box
                role="cell"
                sx={{ order: { xs: 5, md: 0 }, gridColumn: { xs: '1 / -1', md: 'auto' }, justifySelf: { md: 'end' } }}
              >
                <Button
                  size="small"
                  startIcon={<DownloadOutlinedIcon />}
                  onClick={() => void download(invoice)}
                  disabled={!invoice.downloadUrl || downloading === invoice.id}
                  aria-label={`Download invoice ${invoice.invoiceNumber}`}
                  sx={{ ml: { xs: -1, md: 0 } }}
                >
                  {downloading === invoice.id ? 'Downloading…' : 'Download'}
                </Button>
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  }

  return (
    <Section title="Invoices" description="Receipts for your payments, including tax." disablePadding>
      {downloadError && (
        <Box sx={{ px: 2.5, pt: 2 }}>
          <Alert severity="error" onClose={() => setDownloadError(null)}>
            {downloadError}
          </Alert>
        </Box>
      )}
      {body}
    </Section>
  );
};

export default InvoicesSection;
